import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import type { NotificationItem } from '@/types';
import { useNotifications } from './notification-context';

function unreadLabel(count: number): string | null {
  if (count <= 0) {
    return null;
  }
  return count > 9 ? '9+' : String(count);
}

function whenLabel(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

/** Bell in the page header. Opening the list does not mark anything read. */
export function NotificationBell() {
  const { inbox, isOpen, openInbox, closeInbox } = useNotifications();
  const label = unreadLabel(inbox?.unreadCount ?? 0);

  return (
    <>
      <button
        type="button"
        aria-label={label ? `Notifications, ${label} unread` : 'Notifications'}
        aria-expanded={isOpen}
        onClick={() => (isOpen ? closeInbox() : openInbox())}
        className="relative grid size-9 shrink-0 place-items-center rounded-full border border-border text-content transition hover:border-primary/50"
      >
        <BellIcon />
        {label ? (
          <span className="absolute -right-1 -top-1 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[9px] font-extrabold leading-4 text-on-danger">
            {label}
          </span>
        ) : null}
      </button>
    </>
  );
}

export function NotificationInbox() {
  const navigate = useNavigate();
  const { inbox, isOpen, closeInbox, markRead, markAllRead } = useNotifications();
  if (!isOpen) {
    return null;
  }
  const items = inbox?.items ?? [];
  const today = items.filter((item) => isToday(item.createdAt));
  const earlier = items.filter((item) => !isToday(item.createdAt));

  const openItem = async (item: NotificationItem) => {
    closeInbox();
    await markRead(item);
    navigate(item.href);
  };

  return (
    <div className="fixed inset-0 z-40" onClick={closeInbox}>
      <div
        role="dialog"
        aria-label="Notifications"
        onClick={(event) => event.stopPropagation()}
        className="absolute right-3 top-16 flex max-h-[min(32rem,calc(100dvh-6rem))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-panel border border-border bg-canvas shadow-[0_16px_40px_rgba(0,0,0,0.35)] sm:right-6"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="text-sm font-extrabold">Notifications</h2>
          <button
            type="button"
            onClick={() => void markAllRead()}
            className="text-[11px] font-extrabold uppercase tracking-[0.04em] text-primary"
          >
            Mark all as read
          </button>
        </div>
        <div className="overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-content-muted">
              No notifications yet.
            </p>
          ) : (
            <>
              <InboxGroup title="Today" items={today} onOpen={openItem} />
              <InboxGroup title="Earlier" items={earlier} onOpen={openItem} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InboxGroup({
  title,
  items,
  onOpen,
}: {
  title: string;
  items: NotificationItem[];
  onOpen: (item: NotificationItem) => void;
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <section>
      <h3 className="px-4 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-content-muted">
        {title}
      </h3>
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onOpen(item)}
              className={cn(
                'flex w-full flex-col gap-1 px-4 py-3 text-left transition hover:bg-white/5',
                item.expired && 'opacity-50',
              )}
            >
              <span className="flex items-center gap-2">
                {!item.read && !item.expired ? (
                  <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                ) : null}
                <span className="text-[13px] font-bold">{item.title}</span>
                {item.expired ? (
                  <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-content-muted">
                    Expired
                  </span>
                ) : null}
              </span>
              <span className="text-[12px] leading-snug text-content-muted">{item.body}</span>
              <span className="font-mono text-[10px] text-content-muted">
                {whenLabel(item.createdAt)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true" fill="none">
      <path
        d="M6 9.5a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5H4.5S6 13.5 6 9.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M10 18a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
